import React, { useState, useEffect, useRef } from "react";
import { useOrganization } from "@clerk/clerk-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "src/components/ui/switch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Video, VideoOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
// [tutorial disabled] import { driver } from "driver.js";
// [tutorial disabled] import "driver.js/dist/driver.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useApiFetch, errorMessage, toastError } from "src/lib/api";
import LoadingIndicator from "../components/common/LoadingIndicator";
import useInView from "src/hooks/useInView";
import "../styles/Livestreaming.css";
// import { waitFor, waitForElementToBeRemoved } from "@testing-library/react";

// const RETRY_DELAY_MS = 3000;              // retry when URL not ready
// const STALL_TIMEOUT_MS = 10000;           // reload if stuck > 10s
// const DISABLE_PROCESS_TIMEOUT_MS = 20000; // after 20s without progress, disable
// const STALL_CHECK_MS = 3000;

// const VideoPlayer = ({ mp4Url }) => {
//   const videoRef = useRef(null);

//   useEffect(() => {
//     const video = videoRef.current;
//     if (!video || !mp4Url) return;

//     let cancelled = false;
//     let retryTimeout;
//     let stallInterval;
//     let lastProgressTime = Date.now();

//     const tryPlay = async () => {
//       if (cancelled) return;
//       try {
//         await video.play();
//       } catch (_) {
//         // autoplay may be blocked
//       }
//     };

//     const loadSource = () => {
//       if (cancelled) return;
//       console.log("[VideoPlayer] Loading MP4:", mp4Url);
//       video.src = mp4Url;
//       video.preload = "auto";
//       video.muted = true;
//       video.load();
//       // lastProgressTime is only updated on real progress
//     };

//     const handleCanPlay = () => {
//       console.log("[VideoPlayer] MP4 ready, playing");
//       tryPlay();
//     };

//     const handleError = () => {
//       if (cancelled) return;
//       console.warn(
//         "[VideoPlayer] Error loading MP4, retrying in",
//         RETRY_DELAY_MS,
//         "ms"
//       );
//       clearTimeout(retryTimeout);
//       retryTimeout = setTimeout(loadSource, RETRY_DELAY_MS);
//     };

//     const handleTimeUpdate = () => {
//       lastProgressTime = Date.now(); // real playback progress
//     };

//     const checkStall = () => {
//       if (cancelled) return;
//       const now = Date.now();

//       if (video.ended) return; // finished is fine

//       const timeSinceProgress = now - lastProgressTime;

//       if (timeSinceProgress > DISABLE_PROCESS_TIMEOUT_MS) {
//         console.warn("[VideoPlayer] Disabled process detected");
//         clearTimeout(retryTimeout);

//         toast.info("Livestreams disabled successfully, reload page to get latest version", {
//           position: "top-right",
//           autoClose: 5000,
//           hideProgressBar: false,
//           closeOnClick: true,
//           pauseOnHover: true,
//           draggable: true,
//           progress: undefined,
//           zIndex: 1000,
//         });

//         // Optionally stop checking:
//         clearInterval(stallInterval);

//       } else if (timeSinceProgress > STALL_TIMEOUT_MS) {
//         console.warn("[VideoPlayer] Detected stall, reloading source");
//         loadSource();
//       }
//     };

//     video.addEventListener("canplay", handleCanPlay);
//     video.addEventListener("error", handleError);
//     video.addEventListener("timeupdate", handleTimeUpdate);

//     loadSource(); // first attempt
//     stallInterval = setInterval(checkStall, STALL_CHECK_MS);

//     return () => {
//       cancelled = true;
//       clearTimeout(retryTimeout);
//       clearInterval(stallInterval);
//       video.pause();
//       video.removeEventListener("canplay", handleCanPlay);
//       video.removeEventListener("error", handleError);
//       video.removeEventListener("timeupdate", handleTimeUpdate);
//       video.removeAttribute("src");
//       video.load();
//     };
//   }, [mp4Url]);

//   return (
//     <div>
//       <video
//         ref={videoRef}
//         controls
//         style={{ width: "200%", height: "100%", maxHeight: "150px", maxWidth: "300px" }}
//       />
//     </div>
//   );
// };

const CONNECT_TIMEOUT_MS = 45000; // No camera image in this time: the stream could not be enabled
const FRAME_PROBE_MS = 3000; // How often to check whether the camera is sending video
const FRAME_PROBE_TIMEOUT_MS = 10000; // A snapshot request that takes longer is abandoned
const RELOAD_PAGE_MS = 15000; // Reload the stream page (fresh token) while no video arrives

// Shown in place of the player when the camera did not send any video in time
const LivestreamUnavailable = ({ onRetry }) => (
  <div className="livestream-player-unavailable" role="status">
    <VideoOff className="h-8 w-8" />
    <span>This livestream could not be enabled</span>
    <span className="text-xs">
      No video was received from the camera in {CONNECT_TIMEOUT_MS / 1000} seconds.
    </span>
    {onRetry && (
      <Button size="sm" variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    )}
  </div>
);

/*
 * go2rtc snapshot of the stream, which only loads once the camera is really sending video.
 * The play URL is `<base>/auth?st=<token>&next=/stream.html?src=<id>`; the snapshot lives next to
 * stream.html (`api/frame.jpeg`), the same way stream.html itself reaches `api/ws`.
 */
const frameUrlFor = (playUrl) => {
  try {
    const url = new URL(playUrl);
    const next = url.searchParams.get("next");
    if (!next) return null;
    const page = new URL(next, url.origin);
    const src = page.searchParams.get("src");
    if (!src) return null;
    return new URL(`api/frame.jpeg?src=${encodeURIComponent(src)}`, page).toString();
  } catch (_) {
    return null;
  }
};

/*
 * go2rtc player. Like the detection videos, nothing is requested until the card is (close to)
 * on screen. The loading tile stays until a real camera image is received, not just until the
 * stream page loads: right after enabling, the camera can take a while to start publishing.
 * While waiting, the stream page is reloaded with a fresh token; after CONNECT_TIMEOUT_MS the
 * stream is reported as not enabled.
 */
const Go2RtcIframePlayer = ({ playUrl, onConnectingChange }) => {
  const containerRef = useRef(null);
  const inView = useInView(containerRef);
  const [status, setStatus] = useState("connecting"); // "connecting" | "ready" | "failed"
  const [attempt, setAttempt] = useState(0); // Restarts the connection (Retry button)
  const [elapsed, setElapsed] = useState(0); // Seconds since the connection started
  // The iframe keeps the URL it was loaded with: a refreshed playUrl (new token) must not reload it
  const [pageSrc, setPageSrc] = useState(null);
  const [pageKey, setPageKey] = useState(0);
  const pageSrcRef = useRef(null);
  const pageLoadedRef = useRef(false);
  const playUrlRef = useRef(playUrl); // Latest playUrl, with the freshest token
  playUrlRef.current = playUrl;

  // Report to the page while the loading tile is shown, so no other stream is enabled meanwhile
  const connecting = inView && status === "connecting";
  const onConnectingChangeRef = useRef(onConnectingChange);
  onConnectingChangeRef.current = onConnectingChange;
  useEffect(() => {
    if (!connecting) return;
    onConnectingChangeRef.current?.(true);
    return () => onConnectingChangeRef.current?.(false);
  }, [connecting]);

  // Connection attempt: load the stream page, then poll for a camera image until one arrives
  useEffect(() => {
    if (!connecting) return;

    let cancelled = false;
    const startedAt = Date.now();
    let lastReload = 0;
    let lastProbe = 0;
    let probe = null; // { img, startedAt } while a snapshot request is in flight

    const reloadPage = () => {
      lastReload = Date.now();
      pageLoadedRef.current = false;
      pageSrcRef.current = playUrlRef.current || null;
      setPageSrc(pageSrcRef.current);
      setPageKey((key) => key + 1);
    };

    const probeFrame = () => {
      const frameUrl = playUrlRef.current && frameUrlFor(playUrlRef.current);
      // The session cookie is set by the stream page, so wait until it has loaded
      if (!frameUrl || !pageLoadedRef.current) return;
      if (probe) {
        if (Date.now() - probe.startedAt < FRAME_PROBE_TIMEOUT_MS) return;
        probe.img.src = "";
        probe = null;
      }
      lastProbe = Date.now();
      const img = new Image();
      probe = { img, startedAt: lastProbe };
      img.onload = () => {
        probe = null;
        if (!cancelled && img.naturalWidth > 0) setStatus("ready");
      };
      img.onerror = () => {
        probe = null;
      };
      img.src = `${frameUrl}&t=${lastProbe}`;
    };

    const tick = () => {
      if (cancelled) return;
      const now = Date.now();
      setElapsed(Math.floor((now - startedAt) / 1000));
      if (now - startedAt >= CONNECT_TIMEOUT_MS) {
        setStatus("failed");
        return;
      }
      if (now - lastReload >= RELOAD_PAGE_MS || (!pageSrcRef.current && playUrlRef.current)) reloadPage();
      if (now - lastProbe >= FRAME_PROBE_MS) probeFrame();
    };

    setElapsed(0);
    reloadPage();
    const intervalId = setInterval(tick, 1000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      if (probe) probe.img.src = "";
    };
  }, [connecting, attempt]);

  const handlePageLoad = () => {
    pageLoadedRef.current = true;
    // Play URL in an unknown format: no snapshot to check, the loaded page is the best signal
    if (!frameUrlFor(pageSrc ?? "")) setStatus((prev) => (prev === "connecting" ? "ready" : prev));
  };

  // Token renewal: the go2rtc session cookie is short-lived, so every refreshed playUrl (new
  // token, see the periodic refresh of the page) is requested again to renew it, without
  // reloading the player that is already showing video
  useEffect(() => {
    if (status !== "ready" || !playUrl || playUrl === pageSrc) return;
    fetch(playUrl, { mode: "no-cors", credentials: "include", cache: "no-store" }).catch(() => {});
  }, [playUrl, pageSrc, status]);

  const retry = () => {
    setStatus("connecting");
    setAttempt((prev) => prev + 1);
  };

  // 16:9 frame, the iframe fills it (see .livestream-player in Livestreaming.css)
  return (
    <div className="livestream-player" ref={containerRef}>
      {status === "failed" ? (
        <LivestreamUnavailable onRetry={retry} />
      ) : inView && pageSrc ? (
        <iframe
          key={pageKey}
          src={pageSrc}
          title="Live stream"
          allow="autoplay; fullscreen; encrypted-media"
          allowFullScreen
          onLoad={handlePageLoad}
        />
      ) : (
        <div className="skeleton-block absolute inset-0 rounded-none" />
      )}
      {status === "connecting" && (
        <LoadingIndicator
          variant="tile"
          className="livestream-connecting"
          label={inView ? `Connecting to the camera… ${elapsed}s` : null}
        />
      )}
    </div>
  );
};

/* Placeholder rows with the page structure, shown while the livestreams load */
const LivestreamsSkeleton = () => (
  <div className="livestreaming-list" aria-busy="true" aria-label="Loading livestreams">
    {[0, 1].map((row) => (
      <div key={row} className="livestream-row">
        <div className="livestream-row-content">
          <Card className="livestream-info-card">
            <div className="livestream-card-fields">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="min-w-0">
                  <div className="skeleton-block mb-2 h-4 w-20" />
                  <div className="skeleton-block h-5 w-32 max-w-full" />
                </div>
              ))}
            </div>
          </Card>
          <Card className="livestream-stream-card">
            <CardHeader className="livestream-card-header flex-row space-y-0">
              <div className="skeleton-block h-6 w-40 max-w-full" />
              <div className="skeleton-block h-6 w-11 rounded-full" />
            </CardHeader>
            <CardContent>
              <div className="skeleton-block w-full" style={{ aspectRatio: "16 / 9" }} />
            </CardContent>
          </Card>
        </div>
      </div>
    ))}
  </div>
);

const INITIAL_STREAMS = 3; // Livestreams shown before "Load more"
const STREAMS_PER_LOAD = 3; // Livestreams added by each "Load more"
const TOKEN_REFRESH_MS = 4 * 60 * 1000; // Refresh while a stream is on (go2rtc token lasts 5 min)
const FULL_REFRESH_MS = 10 * 60 * 1000; // Refresh while every stream is off
const REQUEST_GRACE_MS = 2 * 60 * 1000; // After this, the backend state wins over an unconfirmed toggle

const Livestreaming = () => {
  const { organization } = useOrganization(); // Get the active organization
  const [livestreams, setLivestreams] = useState([]); // Store livestream URLs
  const [monitorIdFilter, setMonitorIdFilter] = useState(""); // Monitor ID filter
  const [visibleCount, setVisibleCount] = useState(INITIAL_STREAMS); // Lazy loading of the list
  const [loading, setLoading] = useState(true); // Loading state
  const orgId = organization?.id;
  const navigate = useNavigate(); // Hook para redirección
  const [fetchError, setFetchError] = useState(null); // Error message of the last full load
  const [switchStates, setSwitchStates] = useState({});
  // Toggle in progress: { monitorId, enabling } or null. Only one toggle at a time.
  const [pendingToggle, setPendingToggle] = useState(null);
  // Ref mirror of pendingToggle, so handlers and the interval never read a stale value
  const pendingToggleRef = useRef(null);
  // Streams whose player is still showing the loading tile: { [monitorId]: true }
  const [connectingStreams, setConnectingStreams] = useState({});
  // States set by the user that the backend has not confirmed yet: { [monitorId]: { value, at } }.
  // Right after a toggle the backend can still report the previous value, also for streams toggled
  // before, so a refresh must not switch them back.
  const requestedStatesRef = useRef({});
  const emptyToastShownRef = useRef(false); // "No livestreams" toast is shown only once
  const apiFetch = useApiFetch();

  // [tutorial disabled] const disableTutorial = async () => {
    // [tutorial disabled] try {
      // [tutorial disabled] await apiFetch("/auth/disable-tutorial", { method: "POST" });
    // [tutorial disabled] } catch (error) {
      // [tutorial disabled] console.error("Error disabling tutorial:", error);
      // [tutorial disabled] toastError(error);
    // [tutorial disabled] }
  // [tutorial disabled] };

  const filteredLivestreams = (livestreams ?? []).filter((stream) =>
    (stream?.name ?? "").toLowerCase().includes(monitorIdFilter.toLowerCase())
  );
  const visibleLivestreams = filteredLivestreams.slice(0, visibleCount);
  const hasMore = visibleCount < filteredLivestreams.length;
  const canShowLess = visibleCount > INITIAL_STREAMS && filteredLivestreams.length > INITIAL_STREAMS;

  const changeFilter = (value) => {
    setMonitorIdFilter(value);
    setVisibleCount(INITIAL_STREAMS);
  };

  // [tutorial disabled] /* Display tour tutorial */
  // [tutorial disabled] useEffect(() => {
  // [tutorial disabled] let intervalId = null;

  // [tutorial disabled] const checkForTutorialReady = () => {
    // [tutorial disabled] let step = localStorage.getItem("driver-tutorial-step");
    // [tutorial disabled] const el = document.querySelector("#streams");

    // [tutorial disabled] if (step === "livestreaming" && el) {
      // [tutorial disabled] clearInterval(intervalId); // limpiar el polling
      // [tutorial disabled] startDriver(); // lanzar el tour

      // [tutorial disabled] setTimeout(() => {
        // [tutorial disabled] intervalId = setInterval(checkForTutorialReady, 300);
      // [tutorial disabled] }, 1000);
    // [tutorial disabled] }
  // [tutorial disabled] };

  // [tutorial disabled] const startDriver = () => {
    // [tutorial disabled] const driverObj = driver({
      // [tutorial disabled] steps: [
        // [tutorial disabled] {
          // [tutorial disabled] element: "#streams",
          // [tutorial disabled] popover: {
            // [tutorial disabled] title: "Live streams",
            // [tutorial disabled] description: "This section retrieve liveviewing stream from the devices",
            // [tutorial disabled] prevBtnText:  `
              // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                // [tutorial disabled] <path d="M15 18l-6-6 6-6" />
              // [tutorial disabled] </svg>
            // [tutorial disabled] `,
            // [tutorial disabled] doneBtnText:  `
                // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  // [tutorial disabled] <path d="M9 18l6-6-6-6" />
                // [tutorial disabled] </svg>
              // [tutorial disabled] `,
              // [tutorial disabled] nextBtnText:  `
                // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  // [tutorial disabled] <path d="M9 18l6-6-6-6" />
                // [tutorial disabled] </svg>
              // [tutorial disabled] `,
            // [tutorial disabled] onCloseClick: () => {
              // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
              // [tutorial disabled] driverObj.destroy();
              // [tutorial disabled] disableTutorial();
            // [tutorial disabled] },
            // [tutorial disabled] onPrevClick: () => {
              // [tutorial disabled] /* navigate("/index");  */ // Add later when tutorial can be selected 
              // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "navbar");
            // [tutorial disabled] }, 
          // [tutorial disabled] },
          // [tutorial disabled] onDeselected: () => {
            // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
            // [tutorial disabled] disableTutorial();
          // [tutorial disabled] },
          // [tutorial disabled] onDestroyed: () => {
            // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
            // [tutorial disabled] disableTutorial();
          // [tutorial disabled] },
          // [tutorial disabled] onHighlighted: () => {
            // [tutorial disabled] const enablePrev = () => {
              // [tutorial disabled] const prevBtn = document.querySelector('.driver-popover-prev-btn');
              // [tutorial disabled] if (prevBtn) {
                // [tutorial disabled] prevBtn.removeAttribute("disabled");
                // [tutorial disabled] prevBtn.classList.remove('driver-popover-btn-disabled');
                // [tutorial disabled] clearInterval(retry);
              // [tutorial disabled] }
            // [tutorial disabled] };

            // [tutorial disabled] const retry = setInterval(enablePrev, 100);
            // [tutorial disabled] setTimeout(() => clearInterval(retry), 1000);
          // [tutorial disabled] },
        // [tutorial disabled] },
        // [tutorial disabled] {
          // [tutorial disabled] element: "#filter-bar",
          // [tutorial disabled] popover: {
            // [tutorial disabled] title: "Bar to filter livestreams by name",
            // [tutorial disabled] prevBtnText:  `
              // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                // [tutorial disabled] <path d="M15 18l-6-6 6-6" />
              // [tutorial disabled] </svg>
            // [tutorial disabled] `,
            // [tutorial disabled] doneBtnText:  `
                // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  // [tutorial disabled] <path d="M9 18l6-6-6-6" />
                // [tutorial disabled] </svg>
              // [tutorial disabled] `,
              // [tutorial disabled] nextBtnText:  `
                // [tutorial disabled] <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  // [tutorial disabled] <path d="M9 18l6-6-6-6" />
                // [tutorial disabled] </svg>
              // [tutorial disabled] `,
            // [tutorial disabled] onNextClick: () => {
              // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "detections");
              // [tutorial disabled] navigate("/detection");
            // [tutorial disabled] },
            // [tutorial disabled] onCloseClick: () => {
              // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
              // [tutorial disabled] driverObj.destroy();
              // [tutorial disabled] disableTutorial();
            // [tutorial disabled] }
          // [tutorial disabled] },
          // [tutorial disabled] onDeselected: () => {
            // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
            // [tutorial disabled] disableTutorial();
          // [tutorial disabled] },
          // [tutorial disabled] onDestroyed: () => {
            // [tutorial disabled] localStorage.setItem("driver-tutorial-step", "");
            // [tutorial disabled] disableTutorial();
          // [tutorial disabled] },
        // [tutorial disabled] },
        
      // [tutorial disabled] ],
    // [tutorial disabled] });

    // [tutorial disabled] driverObj.drive();
  // [tutorial disabled] };

  // [tutorial disabled] intervalId = setInterval(checkForTutorialReady, 300);

  // [tutorial disabled] return () => {
    // [tutorial disabled] if (intervalId) clearInterval(intervalId);
  // [tutorial disabled] };
  // [tutorial disabled] }, []);


  // Fetch livestream URLs from the backend.
  // silent: refresh in the background (after a toggle / interval) without the full-page loader.
  const fetchLivestreams = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setFetchError(null);
    }

    try {
      const data = await apiFetch("/api/livestream");
      if (!data) return;

      const list = data?.livestreams ?? [];

      /* console.log("[DEBUG] setLivestreams: ", response.data.livestreams);
      console.log("[DEBUG] Setted switchStates: ", switchStates); */

      // Backend state, except where the user's latest request is still pending confirmation
      const requested = requestedStatesRef.current;
      const nextStates = {};
      list.forEach(device => {
        const id = device?.monitorId;
        const backendValue = !!device?.livestreamEnabled;
        const request = requested[id];
        if (request && (request.value === backendValue || Date.now() - request.at > REQUEST_GRACE_MS)) {
          delete requested[id];
        }
        nextStates[id] = requested[id] ? requested[id].value : backendValue;
      });

      // Keep the known playUrl of an enabled stream when the refresh does not return it yet,
      // so its player is not torn down and reconnected
      setLivestreams((prev) => {
        const prevUrls = {};
        (prev ?? []).forEach((device) => { prevUrls[device?.monitorId] = device?.playUrl; });
        return list.map((device) =>
          nextStates[device?.monitorId] && !device?.playUrl && prevUrls[device?.monitorId]
            ? { ...device, playUrl: prevUrls[device.monitorId] }
            : device
        );
      });
      setSwitchStates(nextStates);

      // Backend has no streams at all: notify once (the empty state stays in the page)
      if (!silent && list.length === 0 && !emptyToastShownRef.current) {
        emptyToastShownRef.current = true;
        toast.info("No livestreams available for this organization", { toastId: "no-livestreams" });
      }
    } catch (error) {
      console.error("Error fetching livestreams:", error);
      // A failed full load shows the error state; a failed background refresh keeps the current list
      if (!silent) setFetchError(errorMessage(error, "Error fetching livestreams"));
      toastError(error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Ref mirror of switchStates for the interval below
  const switchStatesRef = useRef(switchStates);
  switchStatesRef.current = switchStates;

  useEffect(() => {
    fetchLivestreams();
    // Periodic background refresh, skipped while a toggle is in progress. While a stream is on it
    // runs before the go2rtc token expires (5 min), so the players can renew their session.
    let lastRefresh = Date.now();
    const intervalId = setInterval(() => {
      if (pendingToggleRef.current) return;
      const anyEnabled = Object.values(switchStatesRef.current).some(Boolean);
      if (Date.now() - lastRefresh < (anyEnabled ? TOKEN_REFRESH_MS : FULL_REFRESH_MS)) return;
      lastRefresh = Date.now();
      fetchLivestreams({ silent: true });
    }, 30 * 1000);

    return () => clearInterval(intervalId);
  }, []);


  // newVal is the value requested by the Switch (true = enable livestream)
  const handleCameraSwitch = async (monitorId, newVal) => {
    // Only one toggle at a time, and no new stream while another one is still connecting
    // (the switches are also disabled in the UI)
    if (pendingToggleRef.current) return;
    if (newVal && connectingStream) return;

    console.log("[INFO] Handling camera switch...");
    const name = (livestreams ?? []).find((stream) => stream?.monitorId === monitorId)?.name;
    const pending = { monitorId, enabling: newVal, name };
    pendingToggleRef.current = pending;
    setPendingToggle(pending);

    try {
      const data = await apiFetch("/api/toggle-livestream-status", {
        method: "POST",
        body: {
          monitorId: monitorId,
          // The backend expects the CURRENT state: `livestream: true` stops the stream and
          // `false` starts it. Sending newVal directly inverts every toggle.
          livestream: !newVal,
        },
      });
      if (!data) return;

      // Refresh the stream data (new playUrl / status) without the full-page loader.
      // The requested value wins over the backend status, which can still be stale right after
      // toggling. The player then waits until the camera really sends video.
      requestedStatesRef.current[monitorId] = { value: newVal, at: Date.now() };
      await fetchLivestreams({ silent: true });

      const message = newVal
        ? `Livestream${name ? ` "${name}"` : ""} enabled, connecting to the camera…`
        : `Livestream${name ? ` "${name}"` : ""} disabled successfully!`;
      toast[newVal ? "info" : "success"](message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        zIndex: 1000,
        id: "toast-disabling"
      });
    } catch (error) {
      console.error("Error changing camera status:", error);
      toastError(error, `Error ${newVal ? "enabling" : "disabling"} livestream`);
    } finally {
      pendingToggleRef.current = null;
      setPendingToggle(null);
    }
  }

  // Called by each player while its loading tile is shown
  const setStreamConnecting = (monitorId, isConnecting) => {
    setConnectingStreams((prev) => {
      if (!!prev[monitorId] === isConnecting) return prev;
      const next = { ...prev };
      if (isConnecting) next[monitorId] = true;
      else delete next[monitorId];
      return next;
    });
  };

  // First stream still connecting (only enabled streams have a mounted player)
  const connectingStream = (livestreams ?? []).find(
    (stream) => connectingStreams[stream?.monitorId] && switchStates[stream?.monitorId]
  );

  // Tooltip shown on a disabled switch
  const switchDisabledReason = (monitorId) => {
    if (pendingToggle) {
      if (pendingToggle.monitorId === monitorId) {
        return `Livestream is being ${pendingToggle.enabling ? "started" : "stopped"}, please wait`;
      }
      return "Another livestream is being updated, please wait until it finishes";
    }
    if (connectingStream && !switchStates[monitorId]) {
      return `Wait until "${connectingStream.name}" finishes connecting to enable another livestream`;
    }
    return undefined;
  };

  // Device details next to the stream, two per row (same distribution as the device cards)
  const livestreamFields = [
    { key: "name", label: "Name" },
    { key: "monitorId", label: "Monitor id" },
    { key: "longitude", label: "Longitude" },
    { key: "latitude", label: "Latitude" },
    { key: "throttle", label: "Throttle" },
    { key: "apoyo_id", label: "Apoyo id", optional: true }, // Only devices that have one
  ];

  return (
    <div className="livestreaming-page container mx-auto px-4 py-8">
      <div className="livestreaming-filter mb-6" id="filter-bar">
        <Input
          type="text"
          placeholder="Filter by Name"
          value={monitorIdFilter}
          onChange={(e) => changeFilter(e.target.value)}
          className="livestreaming-filter-input"
        />
        <Button onClick={() => changeFilter("")} variant="outline">
          Clear
        </Button>
      </div>

      <ToastContainer className="mt-12"/>

      {/* Full-page lock while a livestream is started / stopped: only one toggle at a time */}
      {pendingToggle && (
        <LoadingIndicator
          variant="overlay"
          label={`${pendingToggle.enabling ? "Starting" : "Stopping"} livestream${
            pendingToggle.name ? ` "${pendingToggle.name}"` : ""
          }…`}
          hint="This can take a few seconds. You can't enable or disable another livestream until it finishes."
        />
      )}

      {loading ? (
        <>
          <LoadingIndicator
            label="Loading livestreams…"
            hint="Streams are connected progressively as you scroll."
          />
          <LivestreamsSkeleton />
        </>
      ) : fetchError ? (
        <Card id="devices">
          <CardContent className="livestreaming-message">
            <p className="livestreaming-message-title">Could not load livestreams</p>
            <p className="text-muted-foreground">{fetchError}</p>
            <Button onClick={() => fetchLivestreams()} variant="outline">
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : filteredLivestreams.length === 0 ? (
        <Card id="devices">
          <CardContent className="livestreaming-message">
            {livestreams.length === 0 ? (
              <>
                <p className="livestreaming-message-title">No livestreams available</p>
                <p className="text-muted-foreground">
                  There are no camera devices with livestreaming in this organization yet. Check that your devices are online and registered.
                </p>
              </>
            ) : (
              <>
                <p className="livestreaming-message-title">No livestreams match "{monitorIdFilter}"</p>
                <p className="text-muted-foreground">
                  Try a different name or clear the filter.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="livestreaming-list" id="devices">
          {/* Several streams can be on at once, but they are connected one after the other */}
          {connectingStream && !pendingToggle && (
            <LoadingIndicator
              label={`Connecting to "${connectingStream.name}"…`}
              hint="You can enable another livestream once this one is ready."
            />
          )}

          {visibleLivestreams.map((device) => {
            const isPending = pendingToggle?.monitorId === device.monitorId;
            const disabledReason = switchDisabledReason(device.monitorId);

            // One row per device: details card on the left, stream card on the right
            return (
              <div key={device.id ?? device.monitorId} className="livestream-row" aria-busy={isPending}>
                <div className="livestream-row-content">
                  <Card className="livestream-info-card">
                    <div className="livestream-card-fields">
                      {livestreamFields
                        .filter(({ key, optional }) => !optional || device?.[key])
                        .map(({ key, label }) => (
                          <div key={key} className="min-w-0">
                            <label className="block text-sm font-medium text-gray-500 mb-1">
                              {label}
                            </label>
                            <p className="text-gray-900 break-words">{device?.[key] ?? "Not defined"}</p>
                          </div>
                        ))}
                    </div>
                  </Card>

                  <Card className="livestream-stream-card">
                    <CardHeader className="livestream-card-header flex-row space-y-0">
                      <CardTitle className="livestream-card-title mb-0">
                        <Video className="h-6 w-6 shrink-0" />
                        <span className="text-xl">{/* device?.name?.includes("-") ? device.name.slice(device.name.lastIndexOf("-") + 1) :  */device.name}</span>
                      </CardTitle>
                      {/* Wrapper carries the title: disabled buttons do not get hover events in every browser */}
                      <span title={disabledReason} className="shrink-0">
                        <Switch
                          checked={switchStates[device.monitorId] || false}
                          disabled={!!disabledReason}
                          aria-label={`${switchStates[device.monitorId] ? "Disable" : "Enable"} livestream for ${device.name}`}
                          onCheckedChange={(newVal) => handleCameraSwitch(device.monitorId, newVal)}
                        />
                      </span>
                    </CardHeader>

                    <CardContent>
                      {/* {console.log("[DEBUG] Livestreaming device.mp4Url: ", device.mp4Url)} */}

                      {switchStates[device.monitorId] ? (
                        <Go2RtcIframePlayer
                          key={device.id ?? device.monitorId}
                          playUrl={device.playUrl}
                          onConnectingChange={(isConnecting) => setStreamConnecting(device.monitorId, isConnecting)}
                        />
                      ) : (
                        <div className="livestream-player">
                          <video controls autoPlay muted>
                            Livestream disabled.
                          </video>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            );
          })}

          {(hasMore || canShowLess) && (
            <div className="livestreaming-load-actions">
              {hasMore && (
                <Button variant="outline" className="h-10" onClick={() => setVisibleCount((prev) => prev + STREAMS_PER_LOAD)}>
                  Load more ({filteredLivestreams.length - visibleCount})
                </Button>
              )}
              {canShowLess && (
                <Button variant="ghost" className="h-10" onClick={() => setVisibleCount(INITIAL_STREAMS)}>
                  Show less
                </Button>
              )}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default Livestreaming;
